import { z } from 'zod'

import { FRICTION_SCOPES } from './report'

/**
 * Issue trackers a workspace can send friction items to (docs:
 * guides/friction). One tracker per workspace; none means issue creation is
 * off. GitHub Issues waits for the GitHub App's Issues write permission.
 */
export const FRICTION_ISSUE_TRACKERS = ['linear'] as const

export type FrictionIssueTracker = (typeof FRICTION_ISSUE_TRACKERS)[number]

export const FRICTION_ISSUE_TRACKER_LABELS: Record<
  FrictionIssueTracker,
  string
> = {
  linear: 'Linear',
}

/** The label every friction issue gets; repositories go in the issue body. */
export const FRICTION_ISSUE_LABEL = 'friction'

const RepositoryKeySchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[\w.-]+\/[\w.-]+$/, 'Use owner/name.')

export const LinearTrackerConfigSchema = z.object({
  tracker: z.literal('linear'),
  /** Default team for new issues. */
  teamId: z.string().trim().min(1),
  projectId: z.string().trim().min(1).optional(),
  /** Team per repository (`owner/name`, lowercased); others use `teamId`. */
  repoTeams: z
    .record(RepositoryKeySchema, z.string().trim().min(1))
    .default({}),
})

export type LinearTrackerConfig = z.infer<typeof LinearTrackerConfigSchema>

export const FrictionTrackerConfigSchema = z.discriminatedUnion('tracker', [
  LinearTrackerConfigSchema,
])

export type FrictionTrackerConfig = z.infer<typeof FrictionTrackerConfigSchema>

export const DEFAULT_AUTO_CREATE_MIN_OCCURRENCES = 3

/**
 * Scopes that can create issues automatically. Trevize-scope items are about
 * the platform, so they never go to a workspace's tracker on their own.
 */
export const FRICTION_AUTO_CREATE_SCOPES = [
  'repo',
  'environment',
] as const satisfies readonly (typeof FRICTION_SCOPES)[number][]

export type FrictionAutoCreateScope =
  (typeof FRICTION_AUTO_CREATE_SCOPES)[number]

export const DEFAULT_AUTO_CREATE_SCOPES = FRICTION_AUTO_CREATE_SCOPES

export const FrictionSettingsSchema = z.object({
  /** Null turns issue creation off. */
  tracker: FrictionTrackerConfigSchema.nullable(),
  autoCreate: z.boolean().default(false),
  autoCreateMinOccurrences: z
    .number()
    .int()
    .min(1)
    .max(100)
    .default(DEFAULT_AUTO_CREATE_MIN_OCCURRENCES),
  autoCreateScopes: z
    .array(z.enum(FRICTION_AUTO_CREATE_SCOPES))
    .min(1)
    .default([...DEFAULT_AUTO_CREATE_SCOPES]),
})

export type FrictionSettings = z.infer<typeof FrictionSettingsSchema>

export const DEFAULT_FRICTION_SETTINGS: FrictionSettings = {
  tracker: null,
  autoCreate: false,
  autoCreateMinOccurrences: DEFAULT_AUTO_CREATE_MIN_OCCURRENCES,
  autoCreateScopes: [...DEFAULT_AUTO_CREATE_SCOPES],
}

/** An issue a friction item was sent to. */
export const FrictionExternalIssueSchema = z.object({
  tracker: z.enum(FRICTION_ISSUE_TRACKERS),
  id: z.string(),
  /** Human key, such as `TRE-412`. */
  key: z.string(),
  url: z.string().url(),
})

export type FrictionExternalIssue = z.infer<typeof FrictionExternalIssueSchema>
